import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";
import { ChevronLeft, GitCompare, ArrowRight } from "lucide-react";

import { useCandidates } from "@/application/use-cases/candidates/use-candidates.queries";
import { useAllCandidateChoices } from "@/application/use-cases/candidates/use-candidate-choices.queries";
import { useEvaluationCriteria } from "@/application/use-cases/products/use-criteria.queries";
import { useProducts } from "@/application/use-cases/products/use-products.queries";
import { useAllProductScores } from "@/application/use-cases/products/use-scores.queries";
import { useDefaultWeightsPreset } from "@/application/use-cases/products/use-weights.queries";
import { useAllFeatureProducts } from "@/application/use-cases/products/use-feature-products.queries";
import { useFeaturesList } from "@/application/use-cases/features/use-features.queries";
import { useAllVerdicts } from "@/application/use-cases/feature-roadmap-verdicts/use-verdicts.queries";
import { computeCandidateAggregate } from "@/application/use-cases/candidates/compute-candidate-aggregates";
import { useComponentCriticality } from "@/application/use-cases/candidates/use-component-criticality.queries";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import {
  architectureComponents,
  type ArchitectureComponent,
} from "@/presentation/data/architecture-components";
import { DOMAIN_COLOR, DOMAIN_LABEL } from "@/presentation/data/domains";
import type { CandidateRow, ProductRow } from "@/infrastructure/supabase/database.types";

const searchSchema = z.object({
  a: z.string().optional(),
  b: z.string().optional(),
});

export const Route = createFileRoute("/_app/candidates/compare")({
  validateSearch: searchSchema,
  component: ComparePage,
});

function ComparePage() {
  const search = Route.useSearch();
  const candidatesQ = useCandidates();
  const choicesQ = useAllCandidateChoices();
  const productsQ = useProducts();
  const scoresQ = useAllProductScores();
  const criteriaQ = useEvaluationCriteria();
  const presetQ = useDefaultWeightsPreset();
  const featuresQ = useFeaturesList();
  const fpQ = useAllFeatureProducts();
  const verdictsQ = useAllVerdicts();
  const criticalityQ = useComponentCriticality();

  const candidates = candidatesQ.data ?? [];
  const allChoices = choicesQ.data ?? [];
  const products = productsQ.data ?? [];
  const productScores = scoresQ.data ?? [];
  const criteria = criteriaQ.data ?? [];
  const weights = presetQ.data?.weights ?? {};
  const features = featuresQ.data ?? [];
  const featureProducts = fpQ.data ?? [];
  const verdicts = verdictsQ.data ?? [];
  const criticalityOverrides = criticalityQ.data ?? {};

  const a = candidates.find((c) => c.slug === search.a) ?? null;
  const b = candidates.find((c) => c.slug === search.b) ?? null;

  const choicesA = a ? allChoices.filter((c) => c.candidate_id === a.id) : [];
  const choicesB = b ? allChoices.filter((c) => c.candidate_id === b.id) : [];

  const aggA = useMemo(
    () =>
      a
        ? computeCandidateAggregate({
            choices: choicesA,
            products,
            productScores,
            criteria,
            features,
            featureProducts,
            verdicts,
            weights,
            criticalityOverrides,
          })
        : null,
    [
      a,
      choicesA,
      products,
      productScores,
      criteria,
      features,
      featureProducts,
      verdicts,
      weights,
      criticalityOverrides,
    ]
  );
  const aggB = useMemo(
    () =>
      b
        ? computeCandidateAggregate({
            choices: choicesB,
            products,
            productScores,
            criteria,
            features,
            featureProducts,
            verdicts,
            weights,
            criticalityOverrides,
          })
        : null,
    [
      b,
      choicesB,
      products,
      productScores,
      criteria,
      features,
      featureProducts,
      verdicts,
      weights,
      criticalityOverrides,
    ]
  );

  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const componentById = useMemo(() => new Map(architectureComponents.map((c) => [c.id, c])), []);

  const diff = useMemo(() => {
    if (!a || !b || !aggA || !aggB) return null;
    return computeDiff(choicesA, choicesB, aggA, aggB, componentById, productById);
  }, [a, b, aggA, aggB, choicesA, choicesB, componentById, productById]);

  if (!a || !b) {
    return (
      <ChooseTwo candidates={candidates} currentA={search.a ?? null} currentB={search.b ?? null} />
    );
  }

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <div className="flex items-center gap-2 text-[12px] print:hidden">
        <Link
          to="/candidates"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
        >
          <ChevronLeft className="size-3" />
          All candidates
        </Link>
      </div>

      <RouteHero
        eyebrow="Compare"
        icon={GitCompare}
        numeral={
          aggA && aggB ? `${((aggB.composite ?? 0) - (aggA.composite ?? 0)).toFixed(2)}` : "—"
        }
        numeralTone={
          aggB && aggA && (aggB.composite ?? 0) > (aggA.composite ?? 0) ? "green" : "amber"
        }
        numeralSuffix="delta (B − A)"
        title={`${a.name} ↔ ${b.name}`}
        summary="Only the components that differ between A and B are shown below. The contribution waterfall explains how each swap moved the composite."
        meta={[
          { label: "A · score", value: aggA?.composite?.toFixed(2) ?? "—" },
          { label: "B · score", value: aggB?.composite?.toFixed(2) ?? "—" },
          {
            label: "Components differ",
            value: diff ? `${diff.diffRows.length} / ${architectureComponents.length}` : "—",
          },
          {
            label: "Waterfall sum",
            value: diff ? diff.waterfallSum.toFixed(2) : "—",
            tone: "cyan",
          },
        ]}
      />

      {diff ? (
        <>
          <DiffTable diff={diff} candidateA={a} candidateB={b} />
          <WaterfallView diff={diff} aggA={aggA!} aggB={aggB!} />
        </>
      ) : null}
    </div>
  );
}

interface DiffRow {
  componentId: string;
  componentName: string;
  domain: ArchitectureComponent["domain"];
  criticality: number;
  productA: ProductRow | null;
  productB: ProductRow | null;
  scoreA: number | null;
  scoreB: number | null;
  contributionA: number;
  contributionB: number;
  delta: number; // (B - A) normalised contribution
}

interface DiffResult {
  diffRows: DiffRow[];
  waterfallSum: number;
}

function computeDiff(
  choicesA: { candidate_id: string; component_id: string; product_id: string | null }[],
  choicesB: { candidate_id: string; component_id: string; product_id: string | null }[],
  aggA: ReturnType<typeof computeCandidateAggregate>,
  aggB: ReturnType<typeof computeCandidateAggregate>,
  componentById: Map<string, ArchitectureComponent>,
  productById: Map<string, ProductRow>
): DiffResult {
  const aByComponent = new Map(choicesA.map((c) => [c.component_id, c.product_id]));
  const bByComponent = new Map(choicesB.map((c) => [c.component_id, c.product_id]));
  const allComponents = new Set([...aByComponent.keys(), ...bByComponent.keys()]);
  const aAggByComp = new Map(aggA.componentAggregates.map((c) => [c.componentId, c]));
  const bAggByComp = new Map(aggB.componentAggregates.map((c) => [c.componentId, c]));

  const totalCritA = aggA.totalCriticality || 1;
  const totalCritB = aggB.totalCriticality || 1;

  const diffRows: DiffRow[] = [];
  let waterfallSum = 0;
  for (const id of allComponents) {
    const pidA = aByComponent.get(id) ?? null;
    const pidB = bByComponent.get(id) ?? null;
    if (pidA === pidB) continue;
    const component = componentById.get(id);
    if (!component) continue;
    const aRow = aAggByComp.get(id);
    const bRow = bAggByComp.get(id);
    const normA = aRow ? aRow.contribution / totalCritA : 0;
    const normB = bRow ? bRow.contribution / totalCritB : 0;
    const delta = normB - normA;
    diffRows.push({
      componentId: id,
      componentName: component.name,
      domain: component.domain,
      criticality: aRow?.criticality ?? bRow?.criticality ?? 3,
      productA: pidA ? (productById.get(pidA) ?? null) : null,
      productB: pidB ? (productById.get(pidB) ?? null) : null,
      scoreA: aRow?.score ?? null,
      scoreB: bRow?.score ?? null,
      contributionA: aRow?.contribution ?? 0,
      contributionB: bRow?.contribution ?? 0,
      delta,
    });
    waterfallSum += delta;
  }
  diffRows.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  return { diffRows, waterfallSum };
}

function DiffTable({
  diff,
  candidateA,
  candidateB,
}: {
  diff: DiffResult;
  candidateA: CandidateRow;
  candidateB: CandidateRow;
}) {
  return (
    <div className="glow-card overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3">
        <h2 className="eyebrow">What changes · {diff.diffRows.length} components differ</h2>
      </div>
      <div
        className="text-muted-foreground grid grid-cols-[1.4fr_1fr_1fr_90px] gap-3 bg-[oklch(0.97_0.005_240)] px-5 py-2 text-[10.5px] font-medium tracking-[0.12em] uppercase"
        style={{ borderBottom: "1px solid oklch(0.91 0.008 240)" }}
      >
        <span>Component</span>
        <span>A · {candidateA.name}</span>
        <span>B · {candidateB.name}</span>
        <span className="text-right">Δ contrib.</span>
      </div>
      {diff.diffRows.length === 0 ? (
        <div className="text-muted-foreground p-6 text-center text-[13px] italic">
          No differences — A and B share all component choices.
        </div>
      ) : (
        diff.diffRows.map((row) => <DiffRow key={row.componentId} row={row} />)
      )}
    </div>
  );
}

function DiffRow({ row }: { row: DiffRow }) {
  return (
    <div
      className="grid grid-cols-[1.4fr_1fr_1fr_90px] items-center gap-3 px-5 py-2.5 print:break-inside-avoid"
      style={{ borderTop: "1px solid oklch(0.96 0.005 240)" }}
    >
      <div className="flex flex-col gap-0.5">
        <span className="text-[12.5px] leading-tight font-medium">{row.componentName}</span>
        <span className="text-foreground/55 text-[10.5px]">
          <span
            className="mr-1 inline-block size-1.5 rounded-full"
            style={{ background: DOMAIN_COLOR[row.domain] }}
          />
          {DOMAIN_LABEL[row.domain]} · criticality ×{row.criticality}
        </span>
      </div>
      <ProductCell product={row.productA} score={row.scoreA} />
      <div className="flex items-center gap-1">
        <ArrowRight className="text-foreground/40 size-3" />
        <div className="flex-1">
          <ProductCell product={row.productB} score={row.scoreB} />
        </div>
      </div>
      <span
        className="font-data text-right text-[12px] font-semibold tabular-nums"
        style={{ color: row.delta >= 0 ? "oklch(0.45 0.16 145)" : "oklch(0.55 0.20 25)" }}
      >
        {row.delta >= 0 ? "+" : ""}
        {row.delta.toFixed(3)}
      </span>
    </div>
  );
}

function ProductCell({ product, score }: { product: ProductRow | null; score: number | null }) {
  if (!product) {
    return <span className="text-foreground/45 text-[11.5px] italic">— TBD —</span>;
  }
  return (
    <div className="flex flex-col gap-0.5">
      <span className="line-clamp-1 text-[12px] leading-tight font-medium print:line-clamp-none">
        {product.name}
      </span>
      <span className="text-foreground/55 font-data text-[10.5px]">
        {score !== null ? `score ${score.toFixed(2)}` : "no score"}
      </span>
    </div>
  );
}

function WaterfallView({
  diff,
  aggA,
  aggB,
}: {
  diff: DiffResult;
  aggA: ReturnType<typeof computeCandidateAggregate>;
  aggB: ReturnType<typeof computeCandidateAggregate>;
}) {
  return (
    <div className="glow-card flex flex-col gap-3 p-5">
      <h2 className="eyebrow">Contribution waterfall</h2>
      <p className="text-foreground/60 text-[11.5px]">
        Starting at A's composite, each swap shifts the composite by Δcontrib. The bars sum to (B −
        A).
      </p>
      <div className="grid grid-cols-[80px_1fr_80px] items-center gap-3 text-[11px]">
        <span className="text-foreground/55">A composite</span>
        <div></div>
        <span className="font-data text-right text-[13px] font-semibold tabular-nums">
          {(aggA.composite ?? 0).toFixed(2)}
        </span>
      </div>
      {diff.diffRows.map((row) => (
        <div
          key={row.componentId}
          className="grid grid-cols-[80px_1fr_80px] items-center gap-3 text-[11px] print:break-inside-avoid"
        >
          <span className="text-foreground/55 line-clamp-1 print:line-clamp-none">
            {row.componentName}
          </span>
          <WaterfallBar delta={row.delta} />
          <span
            className="font-data text-right text-[12px] tabular-nums"
            style={{ color: row.delta >= 0 ? "oklch(0.45 0.16 145)" : "oklch(0.55 0.20 25)" }}
          >
            {row.delta >= 0 ? "+" : ""}
            {row.delta.toFixed(3)}
          </span>
        </div>
      ))}
      <div
        className="grid grid-cols-[80px_1fr_80px] items-center gap-3 border-t pt-3 text-[12px]"
        style={{ borderColor: "oklch(0.91 0.008 240)" }}
      >
        <span className="font-semibold">B composite</span>
        <div></div>
        <span className="font-data text-right text-[14px] font-semibold tabular-nums">
          {(aggB.composite ?? 0).toFixed(2)}
        </span>
      </div>
    </div>
  );
}

function WaterfallBar({ delta }: { delta: number }) {
  const absMax = 0.5; // visual scale ceiling — adjust if deltas are usually huge
  const widthPct = Math.min(100, (Math.abs(delta) / absMax) * 100);
  return (
    <div className="relative h-2 w-full">
      <div className="absolute inset-y-0 left-1/2 w-px bg-[oklch(0.91_0.008_240)]" />
      <div
        className="absolute inset-y-0 rounded-sm"
        style={{
          width: `${widthPct / 2}%`,
          left: delta >= 0 ? "50%" : `${50 - widthPct / 2}%`,
          background: delta >= 0 ? "oklch(0.55 0.16 145)" : "oklch(0.62 0.18 25)",
        }}
      />
    </div>
  );
}

function ChooseTwo({
  candidates,
  currentA,
  currentB,
}: {
  candidates: CandidateRow[];
  currentA: string | null;
  currentB: string | null;
}) {
  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-3xl flex-col gap-6 p-10">
      <div className="flex items-center gap-2 text-[12px]">
        <Link
          to="/candidates"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
        >
          <ChevronLeft className="size-3" />
          All candidates
        </Link>
      </div>
      <h1 className="text-2xl font-semibold">Compare two candidates</h1>
      <p className="text-foreground/70 text-[13px]">
        Pick two candidates from below to see their differences side-by-side.
      </p>
      <div className="flex flex-col gap-2">
        {candidates.map((c) => (
          <div key={c.id} className="glow-card flex items-center justify-between gap-3 p-3">
            <span className="text-[13px] font-medium">{c.name}</span>
            <div className="flex gap-2">
              <Link
                to="/candidates/compare"
                search={{ a: c.slug, b: currentB ?? undefined }}
                className="rounded-md border px-3 py-1 text-[11px]"
                style={
                  currentA === c.slug
                    ? {
                        borderColor: "oklch(0.55 0.17 190)",
                        background: "oklch(0.95 0.05 190)",
                        color: "oklch(0.35 0.17 190)",
                      }
                    : { borderColor: "oklch(0.91 0.008 240)" }
                }
              >
                Set as A
              </Link>
              <Link
                to="/candidates/compare"
                search={{ a: currentA ?? undefined, b: c.slug }}
                className="rounded-md border px-3 py-1 text-[11px]"
                style={
                  currentB === c.slug
                    ? {
                        borderColor: "oklch(0.55 0.17 190)",
                        background: "oklch(0.95 0.05 190)",
                        color: "oklch(0.35 0.17 190)",
                      }
                    : { borderColor: "oklch(0.91 0.008 240)" }
                }
              >
                Set as B
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
